import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck, Briefcase, Car, Clock3, Layers, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { PORTAL_LINKS } from '../constants/site';

const BENEFITS = [
  {
    title: 'Access to multiple lenders',
    text: 'Compare lender options for business vehicle files instead of relying on a single policy outcome.',
    icon: Layers,
  },
  {
    title: 'Faster approvals',
    text: 'We package commercial vehicle files clearly to reduce avoidable lender back-and-forth.',
    icon: Clock3,
  },
  {
    title: 'We handle the process',
    text: 'From lender matching to submission follow-up, we manage the process end-to-end.',
    icon: ShieldCheck,
  },
  {
    title: 'Better chance of approval',
    text: 'Your file is matched to lenders that fit commercial borrower and vehicle profile requirements.',
    icon: BadgeCheck,
  },
];

export default function CommercialCarLoansPage() {
  return (
    <div className="min-h-screen pt-24" data-testid="commercial-car-loans-page">
      <SEO
        title="Commercial Car Loans | Business Vehicle Finance"
        description="Commercial car loans for business-use vehicles with lender matching and guided support from enquiry to settlement."
        keywords="commercial car loan, business vehicle finance, company car finance australia"
        canonical="/commercial-car-loans"
      />

      <section className="py-16 sm:py-20" data-testid="commercial-car-loans-hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-500/30 bg-purple-500/10 px-4 py-2 mb-5" data-testid="commercial-car-loans-hero-badge">
              <Briefcase size={14} className="text-purple-300" />
              <span className="text-xs uppercase tracking-[0.14em] text-purple-200">Commercial Car Loans</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.04] text-white" data-testid="commercial-car-loans-hero-heading">
              Commercial Car Loans for Business Use Vehicles
            </h1>
            <p className="mt-5 text-sm sm:text-base text-white/70 max-w-3xl" data-testid="commercial-car-loans-hero-subheading">
              If you’re buying a car for business use, we match your application to lenders that actively fund commercial vehicle files.
            </p>
            <p className="mt-3 text-xs sm:text-sm text-white/60" data-testid="commercial-car-loans-route-priority-text">
              Start with the right product path, then complete your application in our secure portal.
            </p>

            <div className="mt-7 flex flex-wrap gap-3" data-testid="commercial-car-loans-hero-cta-row">
              <a href={PORTAL_LINKS.COMMERCIAL} data-testid="commercial-car-loans-hero-primary-link">
                <Button className="rounded-full bg-gradient-to-r from-purple-700 via-purple-600 to-purple-500 hover:from-purple-600 hover:to-purple-400 text-white font-bold uppercase tracking-[0.14em] text-xs px-7 py-6" data-testid="commercial-car-loans-hero-primary-cta">
                  Check Your Options
                  <ArrowRight size={15} className="ml-2" />
                </Button>
              </a>
              <a href={PORTAL_LINKS.COMMERCIAL} data-testid="commercial-car-loans-hero-secondary-link">
                <Button variant="outline" className="rounded-full border-purple-400/40 bg-navy-900/60 text-white hover:bg-purple-500/15 font-bold uppercase tracking-[0.14em] text-xs px-7 py-6" data-testid="commercial-car-loans-hero-secondary-cta">
                  See What You Can Get Approved For
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 border-y border-purple-500/20 bg-navy-900/70" data-testid="commercial-car-loans-clarity-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="commercial-car-loans-clarity-heading">
            What this product is and who it’s for
          </h2>
          <p className="text-white/75 text-sm sm:text-base leading-relaxed mb-4" data-testid="commercial-car-loans-clarity-text-1">
            Commercial car loans are for businesses and business operators purchasing vehicles used in trading, operations, or client service.
          </p>
          <p className="text-white/75 text-sm sm:text-base leading-relaxed" data-testid="commercial-car-loans-clarity-text-2">
            Instead of applying direct to one lender, we compare options and manage the submission process so you get clearer choices and better lender fit.
          </p>

          <div className="mt-7" data-testid="commercial-car-loans-mid-cta-wrap">
            <a href={PORTAL_LINKS.COMMERCIAL} data-testid="commercial-car-loans-mid-cta-link">
              <Button className="rounded-full bg-purple-600 hover:bg-purple-500 text-white uppercase tracking-[0.13em] text-xs font-bold px-7 py-6" data-testid="commercial-car-loans-mid-cta">
                Check Your Options
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16" data-testid="commercial-car-loans-benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8" data-testid="commercial-car-loans-benefits-heading">
            Why businesses apply through us
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {BENEFITS.map((item) => (
              <article key={item.title} className="rounded-2xl border border-purple-500/20 bg-navy-800/70 p-6" data-testid={`commercial-car-loans-benefit-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <item.icon size={19} className="text-purple-300 mb-3" />
                <h3 className="text-white font-semibold mb-2" data-testid={`commercial-car-loans-benefit-title-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{item.title}</h3>
                <p className="text-sm text-white/70" data-testid={`commercial-car-loans-benefit-text-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20" data-testid="commercial-car-loans-final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" data-testid="commercial-car-loans-final-heading">
            Ready to apply for a commercial car loan?
          </h2>
          <p className="text-white/70 text-sm sm:text-base mb-8" data-testid="commercial-car-loans-final-subheading">
            Start with the commercial car path and we’ll guide the application through to lender-ready submission.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-testid="commercial-car-loans-final-cta-row">
            <a href={PORTAL_LINKS.COMMERCIAL} data-testid="commercial-car-loans-final-primary-link">
              <Button className="w-full sm:w-auto rounded-full bg-gradient-to-r from-purple-700 via-purple-600 to-purple-500 text-white uppercase tracking-[0.14em] text-xs font-bold px-8 py-6" data-testid="commercial-car-loans-final-primary-cta">
                See What You Can Get Approved For
              </Button>
            </a>
            <a href={PORTAL_LINKS.COMMERCIAL} data-testid="commercial-car-loans-final-secondary-link">
              <Button variant="outline" className="w-full sm:w-auto rounded-full border-purple-400/40 bg-navy-900/60 text-white hover:bg-purple-500/15 uppercase tracking-[0.14em] text-xs font-bold px-8 py-6" data-testid="commercial-car-loans-final-secondary-cta">
                Check Your Options
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
